// src/components/leaderboard/UserRankCard.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { UserStats } from '@/types';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface UserRankCardProps {
  userStats: UserStats;
  rank: number;
  previousRank?: number;
}

export const UserRankCard: React.FC<UserRankCardProps> = ({
  userStats,
  rank,
  previousRank,
}) => {
  const getRankChange = () => {
    if (!previousRank) return null;
    const change = previousRank - rank;
    if (change > 0) {
      return (
        <div className="flex items-center gap-1 text-green-500">
          <TrendingUp className="w-4 h-4" />
          <span className="text-sm">+{change}</span>
        </div>
      );
    } else if (change < 0) {
      return (
        <div className="flex items-center gap-1 text-red-500">
          <TrendingDown className="w-4 h-4" />
          <span className="text-sm">{change}</span>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-1 text-muted-foreground">
        <Minus className="w-4 h-4" />
        <span className="text-sm">0</span>
      </div>
    );
  };

  return (
    <motion.div
      initial={{ scale: 0.95, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <Card className="border-2 border-primary bg-gradient-to-br from-primary/10 to-primary/5">
        <CardHeader>
          <CardTitle className="text-lg">Your Rank</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Avatar className="h-16 w-16 ring-4 ring-primary">
              <AvatarImage src={userStats.userPhoto} alt={userStats.userName} />
              <AvatarFallback>{userStats.userName.charAt(0)}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <p className="text-lg font-bold">{userStats.userName}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-3xl font-bold text-primary">#{rank}</span>
                {getRankChange()}
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold">🔥</div>
              <div className="text-lg font-bold">{userStats.currentStreak}</div>
              <div className="text-xs text-muted-foreground">days</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

